/**
 * Production Timeline
 *
 * Swimlane visualisation of production batches showing:
 * - Phases (colonisation, spawn run, incubation, fruiting)
 * - Events (inoculation, transfer, harvest)
 * - Past, present, and future projections
 *
 * Integrated with useTimeline hook for real database data.
 * Phase schedules read from batch_phases table.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { ZoomIn, ZoomOut, ListTodo, LayoutGrid, CalendarDays, Layers, Database, AlertCircle, Plus, ArrowRight, Rows3 } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import Sidebar, { SIDEBAR_PIN_KEY } from '../layout/Sidebar'
import MobileDrawer from '../layout/MobileDrawer'
import { cn } from '@/lib/utils'
import { useTimeline } from '@/hooks/useTimeline'
import { usePhasePrediction } from '@/hooks/useProcessVariance'
import {
  LearningModeBadge,
  ConfidenceIntervalDisplay,
  TrendIndicator,
  BatchTimelineCompact
} from '@/components/production/variance'
import PhaseBackground from '@/components/production/timeline/PhaseBackground'
import EventMarker, { calculateEventPosition } from '@/components/production/timeline/EventMarker'
import EventCard from '@/components/production/timeline/EventCard'
import { SpawnStatusBadge } from '@/components/production/timeline/SpawnPhaseIndicator'
import MaterialShortageAlert from '@/components/production/MaterialShortageAlert'
import { toLocalDateString } from '@/utils/dateUtils'
import BatchProcessView from '@/components/production/BatchProcessView'
import '@/styles/modal-scrollbar.css'

// =============================================================================
// DEMAND-PULL INDICATORS
// =============================================================================

/**
 * Confidence tier badge for demand-driven shadow batches
 * Shows the demand confidence level: recurring (highest), high, medium, low
 */
function ConfidenceTierBadge({ tier }) {
  if (!tier) return null

  const config = {
    recurring: {
      label: 'Recurring',
      className: 'bg-emerald-100 text-emerald-800 border-emerald-300'
    },
    high: {
      label: 'High',
      className: 'bg-blue-100 text-blue-800 border-blue-300'
    },
    medium: {
      label: 'Medium',
      className: 'bg-amber-100 text-amber-800 border-amber-300'
    },
    low: {
      label: 'Low',
      className: 'bg-red-100 text-red-800 border-red-300'
    }
  }

  const tierConfig = config[tier] || config.medium

  return (
    <span className={cn(
      'text-[10px] px-1.5 py-0.5 rounded border font-medium',
      tierConfig.className
    )}>
      {tierConfig.label}
    </span>
  )
}

/**
 * Compact demand source indicator showing target kg and demand week
 */
function DemandSourceIndicator({ demandPull }) {
  if (!demandPull) return null

  const targetKg = demandPull.targetKgFixed || demandPull.targetKgConfidence || 0
  const demandKg = demandPull.demandSourceKg || 0

  if (targetKg === 0 && demandKg === 0) return null

  // Format week as short date
  const weekLabel = demandPull.demandWeekStart
    ? new Date(demandPull.demandWeekStart + 'T00:00:00').toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      })
    : null

  return (
    <div className="text-[10px] text-muted-foreground flex items-center gap-1">
      {targetKg > 0 && (
        <span className="font-medium">{Math.round(targetKg)}kg target</span>
      )}
      {weekLabel && (
        <span className="text-muted-foreground/70">• wk {weekLabel}</span>
      )}
    </div>
  )
}

// =============================================================================
// PHASE DISPLAY CONFIGURATION
// =============================================================================

// Phase colors for legend display (matches useTimeline.js)
const PHASE_COLORS = {
  'Spawn': '#f59e0b',              // amber (colonisation)
  'Incubation': '#60a5fa',         // light blue (cultivation)
  'Fruiting': '#3b82f6',           // blue (primordia/fruiting)
  'Harvest': '#22c55e',            // green (flush harvests)
  'Rest': '#93c5fd',               // lighter blue (rest)
  'Spent': '#6b7280',              // gray
  'Spawn Batch': '#8b5cf6'         // purple (spawn batch colonisation)
}

// Phase labels for legend
const PHASE_LABELS = {
  'Spawn': 'Spawn',
  'Incubation': 'Incubation',
  'Fruiting': 'Fruiting',
  'Harvest': 'Harvest',
  'Rest': 'Rest',
  'Spent': 'Spent',
  'Spawn Batch': 'Spawn Batch'
}

// =============================================================================
// DATABASE TO SWIMLANE TRANSFORMATION
// =============================================================================

/**
 * Transform database batches to swimlane format.
 * Now reads phases directly from batch.phases array (from batch_phases table).
 *
 * The useTimeline hook provides batch.phases with:
 * - startDate, endDate (from planned_start/end)
 * - actualStart, actualEnd (if phase completed)
 * - status, color, textColor
 * - duration, actualDuration
 */
function transformDatabaseBatches(dbBatches) {
  const today = new Date()

  return dbBatches.map(batch => {
    // Use phases from database if available, otherwise fall back to empty
    const phases = batch.phases || []

    // Use timeline events from database if available
    const timelineEvents = batch.timelineEvents || []

    // Calculate overall batch dates from phases
    const startDate = phases.length > 0 ? phases[0].startDate : new Date()
    const endDate = phases.length > 0 ? phases[phases.length - 1].endDate : new Date()

    // Determine batch type (block vs spawn)
    const batchType = batch.batchType || 'block'

    // Determine overall batch status
    const batchStatus = batch.currentState || 'planned'
    let displayStatus = 'planned'

    if (batchType === 'spawn') {
      // Spawn batch status mapping
      if (batchStatus === 'colonised' || batchStatus === 'storage' || batchStatus === 'used') {
        displayStatus = 'completed'
      } else if (batchStatus === 'colonising') {
        displayStatus = 'active'
      } else if (batchStatus === 'contaminated' || batchStatus === 'discarded') {
        displayStatus = 'completed'
      }
    } else {
      // Block batch status mapping (existing logic)
      if (batchStatus === 'completed' || batchStatus === 'harvested') {
        displayStatus = 'completed'
      } else if (batchStatus === 'colonising' || batchStatus === 'fruiting') {
        displayStatus = 'active'
      } else if (phases.some(p => p.status === 'active')) {
        displayStatus = 'active'
      } else if (startDate <= today) {
        displayStatus = 'active'
      }
    }

    // Determine flush count from strain data or default to 2
    const flushCount = batch.strain?.expected_flush_count || batch.strain?.calculated_flush_count || 2

    return {
      id: batch.code || batch.id,
      strain: batch.strain?.name || 'Unknown',
      strainId: batch.strain?.id,
      batchType, // 'block' or 'spawn'
      flushCount,
      startDate,
      endDate,
      phases, // Direct from database (already has correct format from useTimeline)
      timelineEvents, // Direct from database
      status: displayStatus,
      projectedYield: batch.totalHarvestKg || 0,
      allocatedTo: null, // Future: link to orders
      // Shadow batch indicator
      isShadow: batch.isShadow || false,
      // Demand-pull data for shadow batches
      demandPull: batch.demandPull || null,
      // Preserve strain variance for phase prediction
      strainVariance: batch.strainVariance,
      // Spawn-specific fields
      bagCount: batch.bagCount,
      bagsRemaining: batch.bagsRemaining,
      grainType: batch.grainType,
      currentState: batch.currentState,
      // Preserve original data for detail view
      _dbBatch: batch
    }
  }).sort((a, b) => a.startDate - b.startDate)
}

// =============================================================================
// TIMELINE UTILITIES
// =============================================================================

function getDateRange(batches) {
  if (!batches.length) return { start: new Date(), end: new Date() }

  const starts = batches.map(b => b.startDate)
  const ends = batches.map(b => b.endDate)

  // Calculate max flush count to determine padding
  // Each flush cycle adds ~10 days (7 days rest + 3 days harvest)
  const maxFlushCount = Math.max(...batches.map(b => b.flushCount || 2))
  const flushPaddingDays = (maxFlushCount - 1) * 10 // Additional days for flushes beyond first

  // Add padding: flush cycles + 1 week buffer
  const endDate = new Date(Math.max(...ends))
  endDate.setDate(endDate.getDate() + flushPaddingDays + 7)

  return {
    start: new Date(Math.min(...starts)),
    end: endDate
  }
}

function daysBetween(date1, date2) {
  const oneDay = 24 * 60 * 60 * 1000
  return Math.round((date2 - date1) / oneDay)
}

function formatDate(date) {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function formatDateShort(date) {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
}

function isSameDay(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate()
}

/**
 * Get ISO week number and year from a date
 * Returns { week: number, year: number, weekStart: Date }
 */
function getISOWeek(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // Set to nearest Thursday (ISO week starts Monday, week 1 contains Jan 4)
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7))
  const week1 = new Date(d.getFullYear(), 0, 4)
  const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)

  // Calculate week start (Monday)
  const weekStart = new Date(date)
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7))
  weekStart.setHours(0, 0, 0, 0)

  return {
    week: weekNum,
    year: d.getFullYear(),
    weekStart
  }
}

/**
 * Group batches by ISO week based on their start date
 * Returns array of { weekKey, weekLabel, weekStart, batches }
 */
function groupBatchesByWeek(batches) {
  const groups = new Map()

  for (const batch of batches) {
    const { week, year, weekStart } = getISOWeek(batch.startDate)
    const weekKey = `${year}-W${week.toString().padStart(2, '0')}`

    if (!groups.has(weekKey)) {
      // Format: "Wk 12 · 20 Jan"
      const weekLabel = `Wk ${week} · ${weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
      groups.set(weekKey, {
        weekKey,
        weekLabel,
        weekStart,
        batches: []
      })
    }
    groups.get(weekKey).batches.push(batch)
  }

  // Sort groups by week start date
  return Array.from(groups.values()).sort((a, b) => a.weekStart - b.weekStart)
}

// =============================================================================
// SWIMLANE TIMELINE COMPONENT
// =============================================================================

// Check if a batch has any phase that intersects today
function hasActivePhase(batch, today) {
  return batch.phases.some(phase => phase.startDate <= today && phase.endDate >= today)
}

// Height for week header rows
const WEEK_HEADER_HEIGHT = 32

function SwimLaneTimeline({ batches, zoomLevel, onBatchClick, scrollToTodayRef, rowHeight, highlightBatchCode }) {
  const containerRef = useRef(null)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollTimeoutRef = useRef(null)
  const [selectedEvent, setSelectedEvent] = useState(null) // { event, batch, lineage, nextEvent, position }
  const manualScrollOverrideRef = useRef(false) // Temporarily disable linked scrolling
  const isProgrammaticScrollRef = useRef(false) // Track programmatic scroll vs user scroll

  // Group batches by week for sticky headers
  const weekGroups = useMemo(() => groupBatchesByWeek(batches), [batches])

  // Handle scroll state for auto-hiding scrollbars and linked scroll reset
  const handleScroll = useCallback(() => {
    setIsScrolling(true)

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Hide scrollbar after 1 second of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false)
    }, 1000)

    // Reset manual scroll override when user scrolls (not during programmatic scroll)
    // isProgrammaticScrollRef distinguishes scrollToToday from user scrolling
    if (manualScrollOverrideRef.current && !isProgrammaticScrollRef.current) {
      manualScrollOverrideRef.current = false
    }
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const dateRange = useMemo(() => getDateRange(batches), [batches])
  const totalDays = daysBetween(dateRange.start, dateRange.end)

  // Zoom affects how many pixels per day (20px base for readable daily markers)
  const pixelsPerDay = 20 * zoomLevel
  const timelineWidth = totalDays * pixelsPerDay
  const headerHeight = 60
  const labelWidth = 192 // w-48 = 12rem = 192px

  const today = new Date()
  const todayOffset = daysBetween(dateRange.start, today) * pixelsPerDay

  // Generate daily markers with adaptive visibility based on zoom
  const dayMarkers = useMemo(() => {
    const markers = []
    const current = new Date(dateRange.start)
    current.setHours(0, 0, 0, 0)

    // Determine skip interval based on effective pixels per day
    const effectivePPD = pixelsPerDay
    const skipInterval = effectivePPD >= 18 ? 1   // Show all days
                       : effectivePPD >= 12 ? 2   // Every 2nd day
                       : effectivePPD >= 8 ? 5    // Every 5th day
                       : 7                        // Weekly

    let dayIndex = 0
    while (current <= dateRange.end) {
      const day = current.getDate()
      const isToday = isSameDay(current, today)

      // Show if: matches skip interval, is today, or is first of month
      const shouldShow = isToday || day === 1 || (dayIndex % skipInterval === 0)

      markers.push({
        date: new Date(current),
        offset: daysBetween(dateRange.start, current) * pixelsPerDay,
        day,
        isToday,
        isWeekend: current.getDay() === 0 || current.getDay() === 6,
        isFirstOfMonth: day === 1,
        visible: shouldShow
      })
      current.setDate(current.getDate() + 1)
      dayIndex++
    }
    return markers
  }, [dateRange, pixelsPerDay, today])

  // Generate week markers for grid lines (keep for batch row grid lines)
  const weekMarkers = useMemo(() => {
    const markers = []
    const current = new Date(dateRange.start)
    // Round to start of week
    current.setDate(current.getDate() - current.getDay() + 1)

    while (current <= dateRange.end) {
      const offset = daysBetween(dateRange.start, current)
      if (offset >= 0) {
        markers.push({
          date: new Date(current),
          offset: offset * pixelsPerDay
        })
      }
      current.setDate(current.getDate() + 7)
    }
    return markers
  }, [dateRange, pixelsPerDay])

  // Generate month markers
  const monthMarkers = useMemo(() => {
    const markers = []
    const current = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), 1)

    while (current <= dateRange.end) {
      const offset = daysBetween(dateRange.start, current)
      if (offset >= 0) {
        markers.push({
          date: new Date(current),
          offset: offset * pixelsPerDay,
          label: current.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        })
      }
      current.setMonth(current.getMonth() + 1)
    }
    return markers
  }, [dateRange, pixelsPerDay])

  // Find first active batch index
  const firstActiveBatchIndex = useMemo(() => {
    return batches.findIndex(b => b.status === 'active')
  }, [batches])

  // Scroll to show current week (or next week if today is a weekend)
  const scrollToToday = () => {
    if (containerRef.current) {
      // Temporarily disable linked scrolling so it doesn't override our manual scroll
      manualScrollOverrideRef.current = true
      // Mark as programmatic scroll so handleScroll doesn't reset the override
      isProgrammaticScrollRef.current = true

      const dayOfWeek = today.getDay() // 0 = Sunday, 6 = Saturday

      // Calculate target date: Monday of current week, or next Monday if weekend
      let targetDate = new Date(today)
      if (dayOfWeek === 0) {
        // Sunday: scroll to next Monday (+1 day)
        targetDate.setDate(targetDate.getDate() + 1)
      } else if (dayOfWeek === 6) {
        // Saturday: scroll to next Monday (+2 days)
        targetDate.setDate(targetDate.getDate() + 2)
      } else {
        // Weekday: scroll to this week's Monday
        targetDate.setDate(targetDate.getDate() - (dayOfWeek - 1))
      }

      const weekStartOffset = daysBetween(dateRange.start, targetDate) * pixelsPerDay

      // Position target Monday at left edge of timeline (flush with batch column)
      const newScrollLeft = Math.max(0, weekStartOffset)
      containerRef.current.scrollLeft = newScrollLeft

      // Vertical: scroll to the week containing the target date
      // Calculate row offset by summing heights of week groups until we find the target
      let targetScrollTop = 0
      for (const group of weekGroups) {
        // Check if this week group contains our target date
        const weekEnd = new Date(group.weekStart)
        weekEnd.setDate(weekEnd.getDate() + 6) // End of week (Sunday)

        if (targetDate >= group.weekStart && targetDate <= weekEnd) {
          // Found the target week - scroll to position it near the top
          break
        }

        // Add this week's height: 1 week header + N batch rows
        targetScrollTop += WEEK_HEADER_HEIGHT + (group.batches.length * rowHeight)
      }

      containerRef.current.scrollTop = Math.max(0, targetScrollTop)

      // Clear programmatic scroll flag after scroll events have fired
      // This allows handleScroll to detect subsequent user scrolls
      setTimeout(() => {
        isProgrammaticScrollRef.current = false
      }, 100)
    }
  }

  // Expose scrollToToday via ref
  useEffect(() => {
    if (scrollToTodayRef) {
      scrollToTodayRef.current = scrollToToday
    }
  })

  // Auto-scroll to today on mount
  useEffect(() => {
    const timer = setTimeout(scrollToToday, 100)
    return () => clearTimeout(timer)
  }, [])

  // Calculate the horizontal scroll range for linked scrolling
  // First batch center to last batch center
  const scrollRange = useMemo(() => {
    if (batches.length === 0) return { start: 0, end: 0 }

    const firstBatch = batches[0]
    const lastBatch = batches[batches.length - 1]

    const firstCenter = (daysBetween(dateRange.start, firstBatch.startDate) +
                         daysBetween(dateRange.start, firstBatch.endDate)) / 2 * pixelsPerDay
    const lastCenter = (daysBetween(dateRange.start, lastBatch.startDate) +
                        daysBetween(dateRange.start, lastBatch.endDate)) / 2 * pixelsPerDay

    return { start: firstCenter, end: lastCenter }
  }, [batches, dateRange, pixelsPerDay])

  // Linked X-Y scrolling using ScrollTimeline API
  // Animates horizontal scroll based on vertical scroll progress
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Check for ScrollTimeline support
    if (typeof ScrollTimeline === 'undefined') {
      console.log('ScrollTimeline API not available')
      return
    }

    // Calculate the scroll range
    const horizontalRange = scrollRange.end - scrollRange.start
    if (horizontalRange <= 0) return

    // Create a scroll timeline tracking vertical scroll
    const scrollTimeline = new ScrollTimeline({
      source: container,
      axis: 'block'
    })

    // Create a proxy element that we'll animate with transform
    // We use transform because it's animatable, then read its value
    const proxy = document.createElement('div')
    proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;'
    document.body.appendChild(proxy)

    // Animate the proxy's translateX from 0 to horizontalRange
    const animation = proxy.animate(
      [
        { transform: 'translateX(0px)' },
        { transform: `translateX(${horizontalRange}px)` }
      ],
      {
        timeline: scrollTimeline,
        fill: 'both'
      }
    )

    // Read the animated transform value and apply to scrollLeft
    let rafId = null
    let lastX = -1

    const syncScroll = () => {
      // Skip if manual scroll override is active (e.g., user clicked "Today")
      if (manualScrollOverrideRef.current) {
        rafId = requestAnimationFrame(syncScroll)
        return
      }

      const matrix = new DOMMatrix(getComputedStyle(proxy).transform)
      const currentX = matrix.m41 // translateX value

      if (Math.abs(currentX - lastX) > 0.5) {
        lastX = currentX

        const containerWidth = container.clientWidth
        const viewableWidth = containerWidth - labelWidth

        // Map the animated X to scroll position
        const targetCenter = scrollRange.start + currentX
        const targetScrollLeft = Math.max(0, Math.min(
          targetCenter - viewableWidth / 2,
          timelineWidth - viewableWidth
        ))

        container.scrollLeft = targetScrollLeft
      }

      rafId = requestAnimationFrame(syncScroll)
    }

    rafId = requestAnimationFrame(syncScroll)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      animation.cancel()
      proxy.remove()
    }
  }, [scrollRange, labelWidth, timelineWidth])

  // Total content dimensions (label column + timeline width)
  const totalWidth = labelWidth + timelineWidth
  // Calculate total height including week headers
  const totalRows = weekGroups.length + batches.length // week headers + batch rows
  const totalHeight = headerHeight + (weekGroups.length * WEEK_HEADER_HEIGHT) + (batches.length * rowHeight)

  // Build grid template rows: header + alternating week headers and batch rows
  const gridTemplateRows = useMemo(() => {
    const rows = [`${headerHeight}px`]
    for (const group of weekGroups) {
      rows.push(`${WEEK_HEADER_HEIGHT}px`) // Week header
      for (let i = 0; i < group.batches.length; i++) {
        rows.push(`${rowHeight}px`) // Batch row
      }
    }
    return rows.join(' ')
  }, [weekGroups, headerHeight, rowHeight])

  // Key insight: CSS sticky positioning requires the sticky element to be a direct child
  // of the scrolling container, OR the containing block must not interfere.
  //
  // Solution: Use a single CSS Grid for the entire layout where:
  // - Row 1 = Header (sticky top-0)
  // - Rows 2+ = Batch rows
  // - Column 1 = Labels (sticky left-0)
  // - Column 2 = Timeline content
  //
  // The grid itself has the full width to enable horizontal scrolling.

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-full bg-background scrollbar-overlay',
        isScrolling && 'is-scrolling'
      )}
      onScroll={handleScroll}
    >
      {/* Single grid layout - no wrapper divs to break sticky */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: `${labelWidth}px ${timelineWidth}px`,
          gridTemplateRows: gridTemplateRows,
          width: totalWidth,
        }}
      >
        {/* ===== ROW 1: HEADER ===== */}

        {/* Corner cell - sticky both top AND left */}
        <div
          className="sticky top-0 left-0 z-30 border-r border-b border-border p-3 font-medium flex items-center bg-white/30 backdrop-blur-sm"
          style={{ gridRow: 1, gridColumn: 1 }}
        >
          Batches ({batches.length})
        </div>

        {/* Timeline header - sticky top only */}
        <div
          className="sticky top-0 z-20 border-b border-border relative bg-white"
          style={{ gridRow: 1, gridColumn: 2 }}
        >
          {/* Month labels */}
          {monthMarkers.map((marker, i) => (
            <div
              key={i}
              className="absolute top-0 text-sm font-medium text-muted-foreground px-2 py-1 border-l border-border"
              style={{ left: marker.offset }}
            >
              {marker.label}
            </div>
          ))}

          {/* Daily markers with today highlighted */}
          <div className="absolute bottom-0 left-0 right-0 h-6 flex items-end">
            {dayMarkers.filter(m => m.visible).map((marker, i) => (
              <div
                key={i}
                className={cn(
                  "absolute flex items-center justify-center text-xs",
                  marker.isToday
                    ? "bg-red-500 text-white rounded-full w-6 h-6 font-medium"
                    : "text-muted-foreground"
                )}
                style={{
                  left: marker.isToday ? marker.offset - 12 : marker.offset,
                  bottom: marker.isToday ? 1 : 0,
                  opacity: marker.isWeekend && !marker.isToday ? 0.5 : 1
                }}
              >
                {marker.day}
              </div>
            ))}
          </div>

        </div>

        {/* ===== ROWS 2+: WEEK GROUPS WITH STICKY HEADERS ===== */}
        {(() => {
          // Pre-calculate row indices for each week group and batch
          const rowMap = new Map()
          let row = 2 // Start after header row
          for (const group of weekGroups) {
            rowMap.set(`week-${group.weekKey}`, row++)
            for (const batch of group.batches) {
              rowMap.set(`batch-${batch.id}`, row++)
            }
          }

          return weekGroups.map((group) => {
            const weekRow = rowMap.get(`week-${group.weekKey}`)
            return (
            <React.Fragment key={group.weekKey}>
              {/* Week header row - sticky both left AND top for scrolling */}
              <div
                className="sticky left-0 z-[25] bg-white/80 backdrop-blur-sm border-b border-border flex items-center justify-between px-3 font-medium text-sm text-muted-foreground"
                style={{
                  gridRow: weekRow,
                  gridColumn: 1,
                  top: headerHeight // Stick below main header
                }}
              >
                <span>{group.weekLabel}</span>
                {/* US-C2-011: Timeline -> Planner link */}
                <Link
                  to={`/planner?week=${toLocalDateString(group.weekStart)}`}
                  className="text-[10px] text-muted-foreground/60 hover:text-primary flex items-center gap-0.5 transition-colors"
                  title="View in Planner"
                >
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div
                className="sticky z-[24] bg-white border-b border-border relative"
                style={{
                  gridRow: weekRow,
                  gridColumn: 2,
                  top: headerHeight // Stick below main header
                }}
              >
                {/* Week grid lines in header */}
                {weekMarkers.map((marker, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 w-px bg-border/30"
                    style={{ left: marker.offset }}
                  />
                ))}
              </div>

              {/* Batch rows for this week */}
              {group.batches.map((batch) => {
                const isActive = hasActivePhase(batch, today)
                const isSpawn = batch.batchType === 'spawn'
                const isHighlighted = highlightBatchCode && batch.id === highlightBatchCode
                const batchRow = rowMap.get(`batch-${batch.id}`)
                return (
                  <React.Fragment key={batch.id}>
                    {/* Batch label - sticky left only, z-20 to stay above event markers (z-10) */}
                    <div
                      className={cn(
                        'sticky left-0 z-20 border-r border-b border-border/30',
                        'px-3 pl-6 flex items-center cursor-pointer',
                        'hover:bg-muted/50 transition-colors backdrop-blur-sm',
                        isHighlighted
                          ? 'bg-yellow-100/80 ring-2 ring-yellow-400 ring-inset'
                          : batch.isShadow
                            ? isSpawn
                              ? 'bg-purple-50/40 border-l-4 border-l-purple-400 border-dashed'
                              : 'bg-amber-50/40 border-l-4 border-l-amber-400'
                            : isSpawn
                              ? 'bg-purple-50/60 border-l-4 border-l-purple-500'
                              : isActive ? 'bg-blue-50/80' : 'bg-white/80'
                      )}
                      style={{
                        gridRow: batchRow,
                        gridColumn: 1
                      }}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        onBatchClick?.({
                          batch,
                          position: {
                            x: rect.right,
                            y: rect.top + rect.height / 2,
                            targetRight: rect.right,
                            targetTop: rect.top,
                            targetBottom: rect.bottom
                          }
                        })
                      }}
                    >
                      <div className="truncate flex-1 min-w-0 flex items-center gap-1.5">
                        {isSpawn && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 font-medium shrink-0">
                            SB
                          </span>
                        )}
                        <Link
                          to={`/planner?week=${toLocalDateString(batch.startDate)}`}
                          className={cn(
                            'text-sm font-medium truncate hover:text-primary hover:underline transition-colors',
                            batch.isShadow && 'italic text-muted-foreground'
                          )}
                          onClick={(e) => e.stopPropagation()}
                          title="View in Planner"
                        >
                          {/* Remove 's' suffix from shadow batch codes */}
                          {batch.id.replace(/s$/, '')}
                        </Link>
                      </div>
                    </div>

                    {/* Timeline row content */}
                    <div
                      className={cn(
                        'relative border-b border-border/30 overflow-hidden',
                        isHighlighted
                          ? 'bg-yellow-50/60'
                          : batch.isShadow
                            ? isSpawn ? 'bg-purple-50/20' : 'bg-amber-50/30'
                            : isSpawn ? 'bg-purple-50/30'
                            : isActive ? 'bg-blue-50' : 'bg-white'
                      )}
                      style={{
                        gridRow: batchRow,
                        gridColumn: 2
                      }}
                    >
                      {/* Week grid lines */}
                      {weekMarkers.map((marker, i) => (
                        <div
                          key={i}
                          className="absolute top-0 bottom-0 w-px bg-border/30"
                          style={{ left: marker.offset }}
                        />
                      ))}


                      {/* Phase backgrounds - solid colour segments */}
                      {batch.phases.map((phase, phaseIndex) => (
                        <PhaseBackground
                          key={`phase-${phaseIndex}`}
                          phase={phase}
                          variance={{ days: batch.strainVariance?.[`${phase.name}Days`] || 2 }}
                          timelineScale={{
                            rangeStart: dateRange.start,
                            pixelsPerDay
                          }}
                          height={rowHeight}
                          isShadow={batch.isShadow}
                          phaseIndex={phaseIndex}
                        />
                      ))}

                      {/* Event markers - clickable dots at phase boundaries */}
                      {batch.timelineEvents?.map((event, eventIndex) => {
                        const eventDate = event.actualDate || event.plannedDate
                        if (!eventDate) return null

                        const eventPosition = calculateEventPosition(
                          eventDate,
                          dateRange.start,
                          pixelsPerDay
                        )

                        return (
                          <EventMarker
                            key={`event-${eventIndex}`}
                            event={{
                              type: event.eventType,
                              date: eventDate,
                              status: event.actualDate ? 'completed' : 'planned',
                              plannedDate: event.plannedDate,
                              actualDate: event.actualDate
                            }}
                            position={{
                              left: eventPosition,
                              top: rowHeight / 2 // Center vertically in row
                            }}
                            onClick={(eventData, e) => {
                              e.stopPropagation()
                              const rect = e.currentTarget.getBoundingClientRect()
                              const popupHeight = 400
                              const wouldOverflow = rect.bottom + 8 + popupHeight > window.innerHeight

                              // Find next event for this batch
                              const nextEvent = batch.timelineEvents?.find(evt => {
                                const evtDate = evt.actualDate || evt.plannedDate
                                return evtDate && evtDate > eventDate
                              })

                              // Extract data from batch for event card
                              const dbBatch = batch._dbBatch || {}
                              const spawnLineage = dbBatch.spawnLineage || []
                              const substrateLineage = dbBatch.substrateLineage || []
                              const totalBlocks = dbBatch.totalBlocks || 0
                              const bePercent = dbBatch.calculatedVariance?.bePercent || dbBatch.strainVariance?.bePercent || 30

                              // Get LC/spawn source from lineage
                              const lcSource = spawnLineage[0]?.liquidCulture?.code
                              const spawnSource = spawnLineage[0]?.spawnBatch?.code

                              // Calculate expected yield: blocks × average block weight × BE
                              // Assuming ~2.5kg average block weight, BE as decimal
                              const avgBlockWeight = 2.5 // kg
                              const expectedYield = totalBlocks * avgBlockWeight * (bePercent / 100)

                              // Get total spawn mass from lineage
                              const spawnMass = spawnLineage.reduce((sum, s) =>
                                sum + (s.bagsUsed || 0) * (s.bagWeightKg || 0), 0
                              )

                              setSelectedEvent({
                                event: {
                                  type: event.eventType,
                                  date: eventDate,
                                  plannedDate: event.plannedDate,
                                  actualDate: event.actualDate,
                                  // Inoculation event fields - from batch lineage
                                  lcSource: event.lcSource || lcSource,
                                  spawnSource: event.spawnSource || spawnSource,
                                  // Quantity fields - from batch data
                                  quantity: event.quantity || totalBlocks,
                                  format: event.format || 'blocks',
                                  mass: event.mass || (totalBlocks * avgBlockWeight),
                                  // Induction field - calculated from BE
                                  harvestEstimate: event.harvestEstimate || Math.round(expectedYield * 10) / 10,
                                  // Flush fields (will be populated for harvest events)
                                  harvested: event.harvested,
                                  yieldPercent: event.yieldPercent || bePercent,
                                  cumulative: event.cumulative,
                                  // Spent fields
                                  totalHarvest: event.totalHarvest,
                                  finalYield: event.finalYield,
                                  flushCount: event.flushCount || dbBatch.strain?.expected_flush_count,
                                  // Phase dates
                                  phaseEndDate: event.phaseEndDate || nextEvent?.plannedDate,
                                  batchStartDate: event.batchStartDate || batch.startDate
                                },
                                batch: {
                                  id: batch.id,
                                  strain: batch.strain,
                                  totalBlocks,
                                  bePercent,
                                  spawnLineage,
                                  substrateLineage
                                },
                                position: {
                                  x: rect.left + rect.width / 2,
                                  y: rect.bottom + 8,
                                  targetTop: rect.top,
                                  targetBottom: rect.bottom,
                                  flipped: wouldOverflow
                                }
                              })
                            }}
                          />
                        )
                      })}
                    </div>
                  </React.Fragment>
                )
              })}
            </React.Fragment>
          )})
        })()}

      </div>

      {/* Event Detail Card - shown when clicking event markers */}
      {selectedEvent && (
        <EventCard
          event={selectedEvent.event}
          batch={selectedEvent.batch}
          position={selectedEvent.position}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  )
}

// =============================================================================
// PHASE PREDICTION SECTION FOR POPUP
// =============================================================================

/**
 * Map timeline phase names to variance API process types
 */
const PHASE_TO_PROCESS_TYPE = {
  spawn_run: 'spawn',
  incubation: 'incubation',
  pinning: 'fruiting', // Pinning is part of fruiting process
  fruiting: 'fruiting'
}

/**
 * Phase prediction display for the popup
 * Shows EWMA-based expected duration with confidence interval
 */
function PhasePredictionSection({ strainId, phaseName, actualDuration, dayInPhase }) {
  const processType = PHASE_TO_PROCESS_TYPE[phaseName]
  const { prediction, isLoading, error } = usePhasePrediction(strainId, processType)

  // If no strain ID (mock data) or invalid phase, don't show predictions
  if (!strainId || !processType) {
    return null
  }

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground">Loading predictions...</p>
      </div>
    )
  }

  if (error || !prediction) {
    return null // Gracefully hide if predictions unavailable
  }

  const expected = prediction.prediction?.expected_days
  const optimistic = prediction.prediction?.optimistic_days
  const pessimistic = prediction.prediction?.pessimistic_days
  const confidence = prediction.prediction?.confidence_level || 'learning'
  const sampleCount = prediction.prediction?.sample_count || 0
  const trend = prediction.trend?.direction || 'stable'
  const trendConfidence = prediction.trend?.confidence

  const isLearning = sampleCount < 10

  // Calculate if ahead/behind schedule
  let scheduleStatus = null
  if (dayInPhase && expected) {
    const diff = dayInPhase - expected
    if (diff < -1) {
      scheduleStatus = { label: 'Ahead of Schedule', className: 'text-emerald-600' }
    } else if (diff > (pessimistic - expected)) {
      scheduleStatus = { label: 'Behind Schedule', className: 'text-red-600' }
    } else if (diff > 1) {
      scheduleStatus = { label: 'Slightly Behind', className: 'text-amber-600' }
    } else {
      scheduleStatus = { label: 'On Track', className: 'text-blue-600' }
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2">
      {/* Header with learning badge */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Variance Prediction
        </span>
        {isLearning && (
          <LearningModeBadge sampleCount={sampleCount} minSamples={10} showProgress />
        )}
      </div>

      {/* Predicted duration with confidence interval */}
      <div className="flex justify-between items-start">
        <span className="text-muted-foreground text-sm">Expected</span>
        <ConfidenceIntervalDisplay
          expected={expected}
          optimistic={optimistic}
          pessimistic={pessimistic}
          confidence={confidence}
          unit="d"
          showRange={true}
          showConfidenceBadge={!isLearning}
          compact
        />
      </div>

      {/* Schedule status (if active phase) */}
      {scheduleStatus && dayInPhase > 0 && (
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground text-sm">Progress</span>
          <span className={`text-sm font-medium ${scheduleStatus.className}`}>
            {scheduleStatus.label}
          </span>
        </div>
      )}

      {/* Trend indicator (if not learning) */}
      {!isLearning && trend && (
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground text-sm">Trend</span>
          <TrendIndicator
            direction={trend}
            confidence={trendConfidence}
            showLabel
          />
        </div>
      )}

      {/* Historical vs actual comparison */}
      {actualDuration && expected && (
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground text-sm">Actual vs Expected</span>
          <span className={cn(
            'text-sm font-medium',
            actualDuration < optimistic ? 'text-emerald-600' :
            actualDuration > pessimistic ? 'text-red-600' :
            'text-blue-600'
          )}>
            {actualDuration}d vs {expected.toFixed(0)}d
          </span>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// TODAY'S TASKS VIEW
// =============================================================================

function TodaysTasks({ batches }) {
  const today = new Date()

  // Find tasks due today (phases starting or ending today)
  const tasks = useMemo(() => {
    const todayTasks = []

    for (const batch of batches) {
      for (const phase of batch.phases) {
        // Check if phase starts today
        if (phase.startDate.toDateString() === today.toDateString()) {
          todayTasks.push({
            type: 'start',
            batch,
            phase,
            label: `Start ${phase.label}`,
            priority: phase.name === 'fruiting' ? 'high' : 'normal'
          })
        }

        // Check if phase ends today (action needed)
        if (phase.endDate.toDateString() === today.toDateString() && phase.status !== 'completed') {
          todayTasks.push({
            type: 'complete',
            batch,
            phase,
            label: `Complete ${phase.label}`,
            priority: 'high'
          })
        }
      }
    }

    return todayTasks.sort((a, b) => {
      if (a.priority === 'high' && b.priority !== 'high') return -1
      if (b.priority === 'high' && a.priority !== 'high') return 1
      return 0
    })
  }, [batches, today])

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <ListTodo className="w-5 h-5" />
        Today's Tasks ({tasks.length})
      </h3>

      {tasks.length === 0 ? (
        <p className="text-muted-foreground text-sm">No tasks due today.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task, i) => (
            <div
              key={i}
              className={`
                p-3 rounded-lg border cursor-pointer transition-colors
                ${task.priority === 'high'
                  ? 'border-red-300 bg-red-50 dark:bg-red-950/20'
                  : 'border-border hover:bg-muted/50'}
              `}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{task.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {task.batch.id} - {task.batch.strain}
                  </div>
                </div>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: task.phase.color }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// LEGEND
// =============================================================================

function PhaseLegend() {
  return (
    <div className="flex flex-wrap gap-4 p-3 bg-muted/30 rounded-lg">
      {Object.entries(PHASE_LABELS).map(([key, label]) => (
        <div key={key} className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-sm"
            style={{ backgroundColor: PHASE_COLORS[key] }}
          />
          <span className="text-sm">{label}</span>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// PHASES CONFIGURATION
// =============================================================================

function PhasesConfig() {
  const phaseOrder = ['spawn_incubation', 'spawn_run', 'pinning', 'interval', 'spent_substrate']

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <Layers className="w-5 h-5" />
        Production Phases
      </h3>
      <p className="text-muted-foreground text-sm mb-6">
        Configure the phases of your production workflow. Each phase has a typical duration range.
      </p>

      <div className="space-y-4">
        {phaseOrder.map((phaseKey, index) => (
          <div
            key={phaseKey}
            className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
          >
            {/* Order number */}
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
              {index + 1}
            </div>

            {/* Colour indicator */}
            <div
              className="w-4 h-4 rounded-sm flex-shrink-0"
              style={{ backgroundColor: PHASE_COLORS[phaseKey] }}
            />

            {/* Phase info */}
            <div className="flex-1">
              <div className="font-medium">{PHASE_LABELS[phaseKey]}</div>
              <div className="text-sm text-muted-foreground">
                {PHASE_DURATIONS[phaseKey].min} - {PHASE_DURATIONS[phaseKey].max} days typical
              </div>
            </div>

            {/* Duration inputs (read-only for now) */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Min:</span>
              <span className="w-12 text-center font-mono bg-muted px-2 py-1 rounded">
                {PHASE_DURATIONS[phaseKey].min}
              </span>
              <span className="text-muted-foreground">Max:</span>
              <span className="w-12 text-center font-mono bg-muted px-2 py-1 rounded">
                {PHASE_DURATIONS[phaseKey].max}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> Phase configuration is read-only in this prototype.
          Future versions will allow customisation per strain and saving to database.
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

// Row height constant - shared between timeline and scroll calculations
const ROW_HEIGHT = 48

export default function ProductionTimeline({ user, onLogout }) {
  const [searchParams] = useSearchParams()
  const highlightBatchCode = searchParams.get('batch')

  // Fetch real data from database
  const { batches: dbBatches, loading, error, refetch } = useTimeline({ limit: 100 })

  // Transform database batches (empty array if no data)
  const batches = useMemo(() => {
    if (dbBatches && dbBatches.length > 0) {
      return transformDatabaseBatches(dbBatches)
    }
    return []
  }, [dbBatches])

  // Check if we have real data
  const hasData = batches.length > 0

  const [zoomLevel, setZoomLevel] = useState(1)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [view, setView] = useState('timeline') // 'timeline' | 'batches' | 'phases' | 'tasks'
  const scrollToTodayRef = useRef(null)

  // Track sidebar pin state
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(SIDEBAR_PIN_KEY) === 'true'
  })

  useEffect(() => {
    const checkPinState = () => {
      const current = localStorage.getItem(SIDEBAR_PIN_KEY) === 'true'
      if (current !== isPinned) setIsPinned(current)
    }
    const interval = setInterval(checkPinState, 100)
    return () => clearInterval(interval)
  }, [isPinned])

  const handleZoomIn = () => setZoomLevel(z => Math.min(z + 0.25, 3))
  const handleZoomOut = () => setZoomLevel(z => Math.max(z - 0.25, 0.5))
  const handleScrollToToday = () => scrollToTodayRef.current?.()

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar user={user} onLogout={onLogout} />
      </div>

      {/* Mobile Drawer */}
      <div className="md:hidden">
        <MobileDrawer user={user} onLogout={onLogout} />
      </div>

      {/* Main content - full viewport minus sidebar */}
      <div
        className={cn(
          'h-screen flex flex-col transition-all duration-200 ease-out',
          'md:ml-14', // Collapsed sidebar width
          isPinned && 'md:ml-60', // Expanded sidebar width when pinned
          'pt-14 md:pt-0' // Mobile header offset
        )}
      >
        {/* Header */}
        <div className="shrink-0 p-4 border-b border-border bg-background">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Production Timeline</h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Track batch progress through production phases</span>
                {/* Data status indicator */}
                {loading ? (
                  <span className="text-xs px-2 py-0.5 bg-muted rounded-full animate-pulse">Loading...</span>
                ) : error ? (
                  <span className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Error loading data
                  </span>
                ) : hasData ? (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                    <Database className="w-3 h-3" />
                    {batches.length} batch{batches.length !== 1 ? 'es' : ''}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex border border-border rounded-lg overflow-hidden">
                <button
                  className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'timeline' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  onClick={() => setView('timeline')}
                >
                  <LayoutGrid className="w-4 h-4" />
                  Timeline
                </button>
                <button
                  className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'batches' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  onClick={() => setView('batches')}
                >
                  <Rows3 className="w-4 h-4" />
                  Batches
                </button>
                <button
                  className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'phases' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  onClick={() => setView('phases')}
                >
                  <Layers className="w-4 h-4" />
                  Phases
                </button>
                <button
                  className={`px-3 py-2 text-sm flex items-center gap-1 ${view === 'tasks' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  onClick={() => setView('tasks')}
                >
                  <ListTodo className="w-4 h-4" />
                  Tasks
                </button>
              </div>

              {/* Timeline controls */}
              {view === 'timeline' && (
                <>
                  {/* Today button */}
                  <button
                    className="p-2 border border-border rounded-lg hover:bg-muted flex items-center gap-2 text-sm"
                    onClick={handleScrollToToday}
                    title="Jump to today"
                  >
                    <CalendarDays className="w-4 h-4" />
                    <span className="hidden sm:inline">Today</span>
                  </button>

                  {/* Zoom controls */}
                  <div className="flex items-center gap-1 border border-border rounded-lg">
                    <button
                      className="p-2 hover:bg-muted rounded-l-lg"
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="px-2 text-sm min-w-12 text-center">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button
                      className="p-2 hover:bg-muted rounded-r-lg"
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Legend */}
          <PhaseLegend />

          {/* Material shortage alert - show when shortfalls exist */}
          <MaterialShortageAlert className="mt-4" />
        </div>

        {/* Content - fills remaining height */}
        <div className="flex-1 min-h-0 relative">
          {/* Empty state when no batches */}
          {!loading && !hasData && (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <LayoutGrid className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No production batches</h2>
              <p className="text-muted-foreground mb-6 max-w-md">
                Production batches will appear here once you start tracking your mushroom cultivation.
                Create your first batch to see it on the timeline.
              </p>
              <div className="flex gap-3">
                <Link
                  to="/production/strains"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Strains
                </Link>
                <Link
                  to="/production/spawn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Start Production
                </Link>
              </div>
            </div>
          )}

          {view === 'timeline' && hasData && (
            <SwimLaneTimeline
              batches={batches}
              zoomLevel={zoomLevel}
              onBatchClick={setSelectedBatch}
              scrollToTodayRef={scrollToTodayRef}
              rowHeight={ROW_HEIGHT}
              highlightBatchCode={highlightBatchCode}
            />
          )}
          {view === 'batches' && hasData && (
            <BatchProcessView batches={dbBatches} />
          )}
          {view === 'phases' && (
            <div className="p-4 max-w-4xl">
              <PhasesConfig />
            </div>
          )}
          {view === 'tasks' && (
            <div className="p-4 max-w-2xl">
              <TodaysTasks batches={batches} />
            </div>
          )}

          {/* Selected batch detail - styled to match EventCard */}
          {selectedBatch && (() => {
            const batch = selectedBatch.batch
            const pos = selectedBatch.position
            const popupWidth = 280
            const popupHeight = 220 // Approximate height
            const arrowSize = 14
            const gap = 8

            // Position popup to the right of the clicked row
            const popupLeft = Math.min(pos.targetRight + gap + arrowSize / 2, window.innerWidth - popupWidth - 8)
            const popupTop = Math.max(8, Math.min(pos.y - popupHeight / 2, window.innerHeight - popupHeight - 8))

            // Arrow points left toward the row
            const arrowTop = Math.max(16, Math.min(pos.y - popupTop, popupHeight - 16))

            return (
              <>
                {/* Backdrop to close popup */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setSelectedBatch(null)}
                  aria-hidden="true"
                />

                {/* Arrow pointing to target */}
                <div
                  className="fixed z-[51] bg-white rotate-45"
                  style={{
                    width: arrowSize,
                    height: arrowSize,
                    left: popupLeft - arrowSize / 2,
                    top: popupTop + arrowTop - arrowSize / 2,
                    borderLeft: '1px solid black',
                    borderBottom: '1px solid black',
                    boxShadow: '-1px 1px 2px -1px rgba(0,0,0,0.05)'
                  }}
                  aria-hidden="true"
                />

                <div
                  className="fixed bg-white border border-black rounded-lg shadow-lg p-4 z-50"
                  style={{
                    left: popupLeft,
                    top: popupTop,
                    width: popupWidth
                  }}
                >
                  {/* Header: Strain + Batch code + type badge */}
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-base flex items-center gap-2">
                        {batch.strain}
                        {batch.batchType === 'spawn' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                            Spawn
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{batch.id.replace(/s$/, '')}</div>
                    </div>
                  </div>

                  {/* Date range */}
                  <div className="border-t border-border pt-3 mt-3">
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div>{formatDateShort(batch.startDate)}</div>
                      <div className="text-muted-foreground">
                        {daysBetween(batch.startDate, batch.endDate)} days
                      </div>
                      <div>{formatDateShort(batch.endDate)}</div>
                    </div>
                  </div>

                  {/* Spawn batch details */}
                  {batch.batchType === 'spawn' && (
                    <div className="border-t border-border pt-3 mt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Status</span>
                        <span className={cn(
                          'font-medium capitalize',
                          batch.currentState === 'colonised' && 'text-green-600',
                          batch.currentState === 'colonising' && 'text-blue-600',
                          batch.currentState === 'contaminated' && 'text-red-600'
                        )}>
                          {batch.currentState}
                        </span>
                      </div>
                      {batch.bagCount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Bags</span>
                          <span className="font-medium">
                            {batch.bagsRemaining ?? batch.bagCount}/{batch.bagCount}
                            {batch.grainType && <span className="text-muted-foreground ml-1">({batch.grainType})</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Projected yield (block batches only) */}
                  {batch.batchType !== 'spawn' && batch.projectedYield > 0 && (
                    <div className="border-t border-border pt-3 mt-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Projected yield</span>
                        <span className="font-medium">{batch.projectedYield}kg</span>
                      </div>
                    </div>
                  )}

                  {/* Demand-pull info for shadow batches */}
                  {batch.isShadow && batch.demandPull && (
                    <div className="border-t border-border pt-3 mt-3 space-y-2">
                      {/* Targets grid */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {batch.demandPull.demandSourceKg > 0 && (
                          <>
                            <span className="text-muted-foreground">Demand</span>
                            <span className="font-medium text-right">{Math.round(batch.demandPull.demandSourceKg)}kg</span>
                          </>
                        )}
                        {batch.demandPull.targetKgFixed > 0 && (
                          <>
                            <span className="text-muted-foreground">Target</span>
                            <span className="font-medium text-right">{Math.round(batch.demandPull.targetKgFixed)}kg</span>
                          </>
                        )}
                        {batch.demandPull.targetBags > 0 && (
                          <>
                            <span className="text-muted-foreground">Target bags</span>
                            <span className="font-medium text-right">{batch.demandPull.targetBags}</span>
                          </>
                        )}
                        {batch.demandPull.targetKgFixed > 0 && batch._dbBatch?.strain?.defaultSpawnRatePercent && (
                          <>
                            <span className="text-muted-foreground">Spawn required</span>
                            <span className="font-medium text-right">
                              {/* spawn_kg = target_kg × spawn_rate% / BE% */}
                              {Math.round(
                                batch.demandPull.targetKgFixed *
                                (batch._dbBatch.strain.defaultSpawnRatePercent / 100) /
                                ((batch._dbBatch.calculatedVariance?.bePercent || batch.strainVariance?.bePercent || 30) / 100)
                              )}kg
                            </span>
                          </>
                        )}
                      </div>

                      {/* Harvest week / demand week */}
                      {batch.demandPull.demandWeekStart && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {batch.batchType === 'spawn' ? 'Demand week' : 'Harvest week'}
                          </span>
                          <span className="font-medium">
                            {new Date(batch.demandPull.demandWeekStart + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* US-C2-011: View in Planner link */}
                  <div className="border-t border-border pt-3 mt-3">
                    <Link
                      to={`/planner?week=${toLocalDateString(batch.startDate)}`}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                      onClick={() => setSelectedBatch(null)}
                    >
                      View in Planner
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      </div>
    </div>
  )
}
